import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ADMIN_MODE_FEATURE_FLAG } from '@/hooks/useAdminModeAccess';

/**
 * What an admin may change on a feature flag in the table. Every flag is fully editable
 * except adminModeEnabled, which can only be given to individual users: its "On for
 * everyone" switch and its broker groups and ledgers can't be changed, and the admin can't
 * remove themselves from its users. (Frontend only; the API doesn't enforce this.)
 *
 * @param {object} flag a feature flag item
 * @param {string} [field] 'users' | 'brokerGroups' | 'ledgers', for canEdit and canRemove
 * @returns {{
 *   canEditActive: boolean, the "On for everyone" switch
 *   canEdit: boolean, add to or remove from this field
 *   canRemove: (id: string) => boolean, remove this entry from this field
 * }}
 */
export const useFeatureFlagRules = (flag, field) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const isAdminModeFlag = flag?.feature === ADMIN_MODE_FEATURE_FLAG;
	const canEdit = !isAdminModeFlag || field === 'users';

	return {
		canEditActive: !isAdminModeFlag,
		canEdit,
		canRemove: (id) => canEdit && !(isAdminModeFlag && id === userDetails?.userId),
	};
};
